// Keeps the native tray's "Recent recordings" (decision #5, clients/desktop/src/tray.rs) in
// sync with the signed-in user's library. Mounted once in App.tsx, like ImpersonationBanner —
// it renders nothing, it just pushes {id, title} over IPC whenever something that could change
// the list happens: sign-in/out, a publish (requestTrayRefresh), or the window regaining focus.
// Rust never sees the session cookie or a token; this fetches with the existing browser session
// and only ever sends the trimmed list.
//
// Org choice: the shell's current org (`/o/:slug`) wins, then the last-active org persisted
// by AppShell, then the first org as a last resort (see lib/lastOrg.ts).
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { GrackleOut, PagedResponse } from "../api/types";
import {
  EVT_NAVIGATE_RECORDING,
  isDesktop,
  isMobileShell,
  listenDesktopPayload,
  setTrayRecent,
} from "../desktop";
import { orgSlugFromPath, resolveActiveOrg } from "../lib/lastOrg";
import { useSession } from "../session";
import { subscribeTrayRefresh } from "../trayRefresh";

const TRAY_RECENT_LIMIT = 5;
const DEBOUNCE_MS = 400;

export default function TraySync() {
  const { user, orgs, loading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Resolved per render (cheap: a regex and one localStorage read) so the effect below re-runs
  // only when the *org* changes, not on every navigation inside it.
  const activeOrgId = resolveActiveOrg(orgs, orgSlugFromPath(location.pathname))?.id ?? null;

  useEffect(() => {
    let unsub: (() => void) | undefined;
    void listenDesktopPayload<string>(EVT_NAVIGATE_RECORDING, (id) => navigate(`/g/${id}`)).then((u) => {
      unsub = u;
    });
    return () => unsub?.();
  }, [navigate]);

  useEffect(() => {
    if (!isDesktop() || isMobileShell()) return;

    async function push() {
      if (loading) return;
      if (!user || !activeOrgId) {
        await setTrayRecent(false, []);
        return;
      }
      try {
        const params = new URLSearchParams({ filter: "all", limit: String(TRAY_RECENT_LIMIT) });
        const res = await api<PagedResponse<GrackleOut>>("GET", `/orgs/${activeOrgId}/grackles?${params.toString()}`);
        await setTrayRecent(
          true,
          res.items.slice(0, TRAY_RECENT_LIMIT).map((g) => ({ id: g.id, title: g.title })),
        );
      } catch {
        // Best-effort: the tray just keeps showing its last-known list.
      }
    }

    function schedule() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void push(), DEBOUNCE_MS);
    }

    schedule();
    window.addEventListener("focus", schedule);
    const unsubscribe = subscribeTrayRefresh(schedule);
    return () => {
      window.removeEventListener("focus", schedule);
      unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, activeOrgId, loading]);

  return null;
}
